import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LabeledField } from '@components/moleculs/labeled-field';
import type { FormTextFieldProps } from '@components/organisms/forms/form-text-field/types';

import { CheckMarkIcon, ErrorMarkIcon } from '../../../icons';
import { TextField } from '../../../moleculs/text-field';

export const FormTextField: React.FC<FormTextFieldProps> = ({
    name,
    isRequired,
    labelProps,
    textFieldProps,
    className,
    isEndAdornment = undefined,
    endAdornmentTrue,
    endAdornmentFalse,
    ...rest
}) => {
    const { control } = useFormContext();

    const iconTrue = useMemo(
        () =>
            endAdornmentTrue ? (
                <React.Fragment>{endAdornmentTrue}</React.Fragment>
            ) : (
                <CheckMarkIcon />
            ),
        [endAdornmentTrue],
    );

    const iconFalse = useMemo(
        () =>
            endAdornmentFalse ? (
                <React.Fragment>{endAdornmentFalse}</React.Fragment>
            ) : (
                <ErrorMarkIcon />
            ),
        [endAdornmentFalse],
    );

    const endAdornment = useMemo(() => {
        if (isEndAdornment === undefined) {
            return <React.Fragment />;
        }

        return isEndAdornment ? (
            <React.Fragment>{iconTrue}</React.Fragment>
        ) : (
            <React.Fragment>{iconFalse}</React.Fragment>
        );
    }, [iconFalse, iconTrue, isEndAdornment]);

    const element = (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <TextField
                    {...textFieldProps}
                    {...field}
                    helperText={fieldState.error?.message || rest.helperText}
                    error={!!fieldState.error?.message}
                    InputProps={{
                        endAdornment,
                        ...textFieldProps?.InputProps,
                    }}
                />
            )}
        />
    );

    if (labelProps?.label) {
        return (
            <LabeledField
                {...labelProps}
                isRequired={isRequired || labelProps?.isRequired}
                className={className}
            >
                {element}
            </LabeledField>
        );
    }

    return element;
};
